import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const LOGO = "https://customer-assets.emergentagent.com/job_verify-mesh/artifacts/2steydoe_NeuroMesh%20Logo%20%282%29.png";

const legalNav = [
  { path: '/privacy-policy', label: 'Privacy Policy' },
  { path: '/terms', label: 'Terms of Service' },
  { path: '/disclaimer', label: 'Disclaimers' },
];

export default function LegalPage({ title, lastUpdated, sections }) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} — NeuroMesh`;
  }, [title]);

  return (
    <div style={{ background: '#000', minHeight: '100vh', color: '#fff', fontFamily: "'Inter', sans-serif" }}>
      {/* Top nav */}
      <nav style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="NeuroMesh" className="w-8 h-8 object-contain" />
            <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, color: '#fff', fontSize: '15px' }}>NeuroMesh</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-xs uppercase tracking-widest"
            style={{ color: '#555', fontFamily: "'JetBrains Mono', monospace", transition: 'color 0.2s' }}
            onMouseEnter={e => e.target.style.color = '#00F0FF'}
            onMouseLeave={e => e.target.style.color = '#555'}
          >
            <ArrowLeft size={14} /> Back to Site
          </Link>
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12 lg:gap-16">

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div style={{ position: 'sticky', top: '96px' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: '#444', fontSize: '10px', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '16px' }}>
                Legal
              </p>
              <nav className="space-y-1">
                {legalNav.map(nav => (
                  <Link
                    key={nav.path}
                    to={nav.path}
                    style={{
                      display: 'block',
                      padding: '8px 12px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      transition: 'all 0.2s',
                      fontWeight: pathname === nav.path ? 500 : 400,
                      color: pathname === nav.path ? '#00F0FF' : '#666',
                      background: pathname === nav.path ? 'rgba(0, 240, 255, 0.06)' : 'transparent',
                      borderLeft: pathname === nav.path ? '2px solid #00F0FF' : '2px solid transparent',
                    }}
                  >
                    {nav.label}
                  </Link>
                ))}
              </nav>
              <div style={{ marginTop: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px' }}>
                <p style={{ fontFamily: "'JetBrains Mono', monospace", color: '#444', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '8px' }}>Contact</p>
                <a href="mailto:legal@neuro-mesh.io" style={{ color: '#00F0FF', fontSize: '12px', fontFamily: "'JetBrains Mono', monospace" }}>
                  legal@neuro-mesh.io
                </a>
              </div>
            </div>
          </aside>

          {/* Main content */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div style={{ marginBottom: '48px', paddingBottom: '32px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: '#00F0FF', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '12px' }}>
                Legal Documentation
              </p>
              <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700, color: '#fff', marginBottom: '12px', lineHeight: 1.1 }}>
                {title}
              </h1>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: '#444', fontSize: '12px' }}>
                Last Updated: {lastUpdated}
              </p>
            </div>

            {/* Mobile legal nav */}
            <div className="lg:hidden flex gap-3 mb-10 overflow-x-auto pb-2">
              {legalNav.map(nav => (
                <Link
                  key={nav.path}
                  to={nav.path}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    border: pathname === nav.path ? '1px solid #00F0FF' : '1px solid rgba(255,255,255,0.1)',
                    color: pathname === nav.path ? '#00F0FF' : '#666',
                    background: pathname === nav.path ? 'rgba(0,240,255,0.06)' : 'transparent',
                  }}
                >
                  {nav.label}
                </Link>
              ))}
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {sections.map((section, i) => (
                <motion.section
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  style={{ paddingBottom: '32px', borderBottom: i < sections.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}
                >
                  <h2 style={{
                    fontFamily: "'Syne', sans-serif",
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#fff',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#00F0FF', fontSize: '12px', fontWeight: 400 }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {section.title}
                  </h2>
                  <div style={{ color: '#999', fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
                    {section.content}
                  </div>
                </motion.section>
              ))}
            </div>

            {/* Footer note */}
            <div style={{ marginTop: '64px', padding: '24px', background: 'rgba(0,240,255,0.03)', border: '1px solid rgba(0,240,255,0.08)', borderRadius: '8px' }}>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", color: '#555', fontSize: '12px', lineHeight: 1.7 }}>
                Thalamus Labs Ltd · Intershore Chambers, Road Town, Tortola, British Virgin Islands ·{' '}
                <a href="mailto:legal@neuro-mesh.io" style={{ color: '#00F0FF' }}>legal@neuro-mesh.io</a>
              </p>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}
